/*
 * <product-form>: wraps the product page layout. Enables the hidden variant id
 * (rendered disabled for multi-variant products so that without JS only the
 * <noscript> select posts `id`) and keeps it, the price, button state, the
 * inquiry trigger's variant (link and product-request dialog), URL and
 * gallery DOM order in sync with the option pills, marks option values with no
 * available variant, and adds to cart over Ajax with an inline error instead
 * of Shopify's error page; on success it renders and opens <cart-drawer> from
 * the `sections` payload, or falls back to the cart page when the drawer is
 * missing or not working. After an uncertain add, the button takes the customer
 * to the cart rather than repeating a potentially completed request.
 * Data comes from the JSON scripts the section embeds;
 * no money formatting happens here.
 */
if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('form.product-form');
        if (!this.form) return;

        const variants = this.readJson('data-variants-json');
        this.variants = Array.isArray(variants) ? variants : [];
        this.gallery = this.readJson('data-gallery-json');
        this.prices = this.readJson('data-prices-json');

        this.idInput = this.form.querySelector('input[name="id"]');
        this.idInput.disabled = false;
        this.radios = Array.from(this.form.querySelectorAll('input[type="radio"][data-option-position]'));
        this.quantity = this.form.querySelector('input[name="quantity"]');
        this.submit = this.form.querySelector('.product-form__submit');
        this.error = this.form.querySelector('[data-form-error]');
        this.payment = this.form.querySelector('.product-form__payment');
        this.priceBox = this.querySelector('[data-product-price]');
        this.galleryRoot = this.querySelector('[data-product-gallery]');
        this.figures = Array.from(this.querySelectorAll('[data-media-id]'));
        this.labels = {
          add: this.form.dataset.labelAdd,
          adding: this.form.dataset.labelAdding,
          soldOut: this.form.dataset.labelSoldOut,
          unavailable: this.form.dataset.labelUnavailable,
        };

        this.form.addEventListener('change', (event) => {
          if (event.target.matches('input[type="radio"][data-option-position]')) this.onOptionChange();
        });
        this.form.addEventListener('click', (event) => this.onStep(event));
        this.form.addEventListener('submit', (event) => this.onSubmit(event));

        // The server renders the button for the whole product: apply the picked variant, and let
        // pills changed before this script ran carry the id, price and gallery along.
        if (this.radios.length) {
          const variant = this.findVariant(this.selectedOptions());
          if (variant && String(variant.id) === this.idInput.value) {
            this.updateAvailability(this.selectedOptions());
            this.setSubmitFor(variant);
          } else {
            this.onOptionChange();
          }
        }
      }

      readJson(attribute) {
        const script = this.querySelector(`script[${attribute}]`);
        try {
          return JSON.parse(script ? script.textContent : '{}');
        } catch {
          return {};
        }
      }

      selectedOptions() {
        const values = [];
        this.radios.forEach((radio) => {
          if (radio.checked) values[Number(radio.dataset.optionPosition) - 1] = radio.value;
        });
        return values;
      }

      findVariant(values) {
        return this.variants.find((variant) => variant.options.every((option, index) => option === values[index]));
      }

      onOptionChange() {
        const values = this.selectedOptions();
        const variant = this.findVariant(values);
        this.updateAvailability(values);
        this.setSubmitFor(variant);
        if (!variant) return;
        this.idInput.value = variant.id;
        this.updateProductContext(variant);
        if (this.priceBox && this.prices[variant.id]) this.priceBox.innerHTML = this.prices[variant.id];
        window.history.replaceState({}, '', `${this.dataset.url}?variant=${variant.id}`);
        this.reorderGallery(variant.id);
      }

      updateProductContext(variant) {
        const help = this.querySelector('.product-form__help');
        if (help) {
          const url = new URL(help.href);
          url.searchParams.set('variant', variant.title);
          url.searchParams.set('sku', variant.sku || '');
          url.searchParams.set('product_url', `${this.dataset.url}?variant=${variant.id}`);
          help.href = url.href;
          help.dataset.productRequestVariant = variant.id;
        }
      }

      updateAvailability(values) {
        this.radios.forEach((radio) => {
          const candidate = values.slice();
          candidate[Number(radio.dataset.optionPosition) - 1] = radio.value;
          const match = this.findVariant(candidate);
          radio.toggleAttribute('data-unavailable', !match || !match.available);
        });
      }

      /* Same slot rule as product-gallery.liquid: wide/half by index and rhythm, a lone last half becomes wide. */
      isWide(index, count, rhythm) {
        const last = index === count - 1;
        if (rhythm === 'stack') return true;
        if (rhythm === 'pairs-first') return index % 3 === 2 || (last && index % 3 === 0);
        return index % 3 === 0 || (last && index % 3 === 1);
      }

      reorderGallery(variantId) {
        if (!this.galleryRoot) return;
        const ids = (this.gallery[variantId] || []).map(String);
        const rank = (figure) => {
          const index = ids.indexOf(figure.dataset.mediaId);
          return index >= 0 ? index : 100 + Number(figure.dataset.position);
        };
        const ordered = this.figures.slice().sort((a, b) => rank(a) - rank(b));
        const rhythm = this.galleryRoot.dataset.rhythm;
        ordered.forEach((figure, index) => {
          figure.classList.toggle('product-gallery__item--wide', this.isWide(index, ordered.length, rhythm));
          this.galleryRoot.appendChild(figure);
        });
        this.galleryRoot.scrollTo({ left: 0 });
      }

      setSubmit(enabled, label) {
        if (!this.submit) return;
        if (this.reviewCart) {
          enabled = true;
          label = this.dataset.reviewLabel;
        }
        this.submit.disabled = this.adding || !enabled;
        if (enabled) this.submit.removeAttribute('aria-disabled');
        else this.submit.setAttribute('aria-disabled', 'true');
        if (label) this.submit.textContent = this.adding ? this.labels.adding : label;
      }

      setSubmitFor(variant) {
        if (this.payment) this.payment.hidden = this.reviewCart || !variant?.available;
        if (!variant) this.setSubmit(false, this.labels.unavailable);
        else this.setSubmit(variant.available, variant.available ? this.labels.add : this.labels.soldOut);
      }

      onStep(event) {
        const button = event.target.closest('[data-step]');
        if (!button || !this.quantity) return;
        const current = Number(this.quantity.value) || 1;
        this.quantity.value = Math.max(1, current + Number(button.dataset.step));
      }

      onSubmit(event) {
        event.preventDefault();
        if (!this.submit || this.submit.disabled || this.adding) return;
        if (this.reviewCart) {
          window.location.assign(this.dataset.cartUrl);
          return;
        }
        this.showError('');
        this.adding = true;
        this.submit.disabled = true;
        this.submit.setAttribute('aria-busy', 'true');
        this.submit.textContent = this.labels.adding;
        const quantity = this.quantity ? Math.max(1, Number(this.quantity.value) || 1) : 1;

        return fetch(this.dataset.cartAddUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(this.idInput.value), quantity }], sections: 'cart-drawer' }),
        })
          .then((response) => response.json().catch(() => ({})).then((data) => ({ ok: response.ok, data })))
          .then(({ ok, data }) => {
            if (!ok) {
              // A 422 can add the remaining stock, and invalid section parameters can return
              // 400 after the add succeeded. An error status does not prove nothing changed.
              this.reviewCart = true;
              this.showError([data?.description || data?.message, this.dataset.reviewMessage].filter(Boolean).join(' '));
              return;
            }
            if (!this.openDrawer(data?.sections?.['cart-drawer'])) {
              this.reviewCart = true;
              window.location.assign(this.dataset.cartUrl);
            }
          })
          .catch(() => {
            // The response may have been lost after Shopify added the item. Keep this
            // form in review mode even if options change or the customer clicks again.
            this.reviewCart = true;
            this.showError(this.dataset.reviewMessage);
          })
          .finally(() => {
            this.adding = false;
            this.submit.removeAttribute('aria-busy');
            const variant = this.radios.length ? this.findVariant(this.selectedOptions()) : this.variants.find((item) => String(item.id) === this.idInput.value);
            this.setSubmitFor(variant);
          });
      }

      // The item is in the cart by now. A drawer that is missing, never upgraded (cart-drawer.js or
      // cart-lines.js failed) or throws must not read as a failed add, which invites a second add:
      // the cart page shows the result instead.
      openDrawer(html) {
        const drawer = document.querySelector('cart-drawer');
        if (!html || typeof drawer?.render !== 'function' || typeof drawer.open !== 'function') return false;
        try {
          drawer.render(html);
          drawer.open(this.submit);
          return true;
        } catch {
          return false;
        }
      }

      showError(message) {
        if (!this.error) return;
        this.error.textContent = message;
        this.error.hidden = !message;
      }
    }
  );
}
